import { useParams } from "react-router-dom";
import styled from "styled-components";
import { memo, useMemo, useState } from "react";
import format from "date-fns/format";

import { useQuery } from "@toolbox/apollo";
import {
  CompanyApplicationsTrustScoresDocument,
  JobExperiencePreference,
  TechnologyPreference,
} from "@toolbox/schema";
import { Loading } from "@otta/shared-components";
import { Card, Heading, Spacing, Text } from "@otta/design";
import { Link } from "@toolbox/components/Link";
import { palette, pxToRem } from "@otta/design-tokens";

const Label = styled.label`
  display: flex;
  gap: 0.5rem;
`;

const Padding = styled.div`
  padding: 1rem;
`;

const Columns = styled.div`
  flex-direction: row;
  display: flex;
  gap: 1rem;
`;

const DL = styled.dl`
  grid-template-columns: min-content auto;
  column-gap: 0.5rem;
  row-gap: 0.2rem;
  display: grid;
`;

const DD = styled.dd`
  white-space: nowrap;
  font-weight: bold;
  grid-column: 1;
`;

const DT = styled.dt`
  grid-column: 2;
`;

const SmallDD = styled(DD)`
  max-width: ${pxToRem(350)};
  text-overflow: ellipsis;
  overflow: hidden;
`;

const SmallDT = styled(DT)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const ColCard = styled(Card)`
  flex-direction: column;
  display: flex;
  flex: 1 1 0;
  gap: 1rem;
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  max-width: ${pxToRem(500)};
  gap: 0.4rem;
`;

const Tag = styled.span<{ pos?: boolean }>`
  padding: 0.2rem 0.8rem;
  border-radius: 1rem;
  display: flex;
  background-color: ${p =>
    p.pos === true
      ? palette.extended.green.shade200
      : p.pos === false
      ? palette.extended.red.shade200
      : palette.beige.shade300};
`;

const TrustHeading = styled(Heading)<{ pos?: boolean }>`
  font-family: "Comic Sans MS", sans-serif;
  -webkit-text-stroke: 0.1rem black;
  color: white;
  max-width: fit-content;
  padding: 2rem 4rem;
  border-radius: 8rem;
  font-size: 6rem;
  background-color: ${p =>
    p.pos ? palette.extended.green.shade400 : palette.extended.red.shade400};
`;

const DodgeRate = styled(Text)`
  margin-left: auto;
`;

const JobExperience = memo(
  ({
    prefs,
    appId,
  }: {
    prefs: Partial<JobExperiencePreference>;
    appId: string;
  }) => {
    return (
      <>
        <Heading>Experience preferences</Heading>
        <TagList>
          {Object.entries(prefs)
            .filter(([, v]) => v === true)
            .map(([k]) => (
              <Tag key={`${appId}_${k}`}>{k}</Tag>
            ))}
        </TagList>
      </>
    );
  }
);

const DateValue = memo(({ value }: { value: string | null }) => (
  <span>{value ? format(new Date(value), "MMM yyyy") : "Present"}</span>
));

export function TrustScores(): React.ReactElement {
  const [candidateId, setCandidateId] = useState<string>("");
  const [onlyReported, setOnlyReported] = useState<boolean>(false);
  const { companyId } = useParams();

  const { data, loading } = useQuery(CompanyApplicationsTrustScoresDocument, {
    variables: {
      id: companyId as string,
      userId: candidateId ? candidateId : null,
      reported: onlyReported ? true : null,
      limit: 150,
    },
  });

  const dodgyRate: number = useMemo(() => {
    let dodge = 0.0;
    const total = data?.company?.jobApplications.length ?? 0;
    for (const app of data?.company?.jobApplications ?? []) {
      dodge += app.candidate.trusted ? 0 : 1;
    }
    return Math.round((dodge / total) * 100);
  }, [data]);

  if (loading) {
    return <Loading />;
  }

  return (
    <Padding>
      <Spacing>
        <Card>
          <Columns>
            <Label>
              Find candidate by ID
              <input
                value={candidateId}
                onChange={e => setCandidateId(e.target.value)}
              />
            </Label>
            <Label>
              <input
                type="checkbox"
                checked={onlyReported}
                onChange={e => setOnlyReported(e.target.checked)}
              />
              Only reported fake
            </Label>
            <DodgeRate>
              <strong>{dodgyRate}% </strong>dodgy candidates
            </DodgeRate>
          </Columns>
        </Card>
        {data?.company?.jobApplications.map(app => (
          <Columns key={app.id}>
            <ColCard>
              <Heading>The Suspect</Heading>
              <DL>
                <DD>Region</DD>
                <DT>{app.candidate.signupRegion}</DT>
                <DD>Name</DD>
                <DT>
                  {app.candidate.firstName} {app.candidate.lastName} -{" "}
                  {app.candidate.id}
                </DT>
                <DD>Email</DD>
                <DT>{app.candidate.email}</DT>
                <DD>LinkedIn</DD>
                <DT>
                  {app.candidate.linkedinUrl ? (
                    <Link to={app.candidate.linkedinUrl} underline>
                      {app.candidate.linkedinUrl}
                    </Link>
                  ) : (
                    "None"
                  )}
                </DT>
                <DD>Salary ask</DD>
                <DT>
                  {app.candidate.minSalaryRequirement?.amount}{" "}
                  {app.candidate.minSalaryRequirement?.currency}
                </DT>
                <DD>seen, saves, clicks</DD>
                <DT>
                  {app.candidate.jobReactionStatistics.numberJobsSeen},{" "}
                  {app.candidate.jobReactionStatistics.numberJobsSaved},{" "}
                  {app.candidate.totalJobApplicationClicks}
                </DT>
                {app.candidate.websiteLinks.map(link => (
                  <>
                    <DD key={`${link.id}_dd`}>{link.linkType}</DD>
                    <DT key={`${link.id}_dt`}>
                      <Link to={link.url} underline>
                        {link.url}
                      </Link>
                    </DT>
                  </>
                ))}
              </DL>
              {!!app.candidate.locationPreferences && (
                <>
                  <Heading>Location preferences</Heading>
                  <TagList>
                    {app.candidate.locationPreferences.map(pref => (
                      <Tag key={`${app.id}_${pref.location}`}>
                        {pref.location}
                      </Tag>
                    ))}
                  </TagList>
                </>
              )}
              {!!app.candidate.technologiesUsedPreferences.length && (
                <>
                  <Heading>Technology preferences</Heading>
                  <TagList>
                    {app.candidate.technologiesUsedPreferences.map(pref => (
                      <Tag
                        key={`${app.id}_${pref.technologyUsed.value}`}
                        pos={
                          pref.technologyPreference !==
                          TechnologyPreference.Negative
                        }
                      >
                        {pref.technologyUsed.value}
                      </Tag>
                    ))}
                  </TagList>
                </>
              )}
              {!!app.candidate.jobFunctionPreferences.length && (
                <>
                  <Heading>Function preferences</Heading>
                  <TagList>
                    {app.candidate.jobFunctionPreferences.map(pref => (
                      <Tag key={`${app.id}_${pref.function.value}`}>
                        {pref.function.value}
                      </Tag>
                    ))}
                  </TagList>
                </>
              )}
              {!!app.candidate.jobSubFunctionPreferences.length && (
                <>
                  <Heading>Subfunction preferences</Heading>
                  <TagList>
                    {app.candidate.jobSubFunctionPreferences.map(pref => (
                      <Tag key={`${app.id}_${pref.subFunction.value}`}>
                        {pref.subFunction.value}
                      </Tag>
                    ))}
                  </TagList>
                </>
              )}
              {!!app.candidate.jobExperiencePreference && (
                <JobExperience
                  prefs={app.candidate.jobExperiencePreference}
                  appId={app.id}
                />
              )}
              {!!app.candidate.workExperiences?.length && (
                <>
                  <Heading>Claimed work experience</Heading>
                  <DL key={app.id}>
                    {app.candidate.workExperiences.map(wex => (
                      <>
                        <SmallDD key={`${wex.id}_dd`}>
                          {wex.companyName}: {wex.title}
                        </SmallDD>
                        <SmallDT key={`${wex.id}_dt`}>
                          <DateValue value={wex.startDate} /> -{" "}
                          <DateValue value={wex.endDate} />{" "}
                          {wex.descriptions?.length
                            ? `"${wex.descriptions[0].value}"`
                            : "[No descs]"}
                        </SmallDT>
                      </>
                    ))}
                  </DL>
                </>
              )}
            </ColCard>
            <ColCard>
              <Heading>
                {app.job.title} ({app.job.id})
              </Heading>
              <TagList>
                {app.job.locationPreferences.map(l => (
                  <Tag key={`${app.job.id}_${l}`}>{l.location}</Tag>
                ))}
              </TagList>
              <Heading>The scores</Heading>
              <TagList>
                {app.trust.scores?.map(({ name, score }) => (
                  <Tag key={`${app.id}_${name}`} pos={score === 0}>
                    {name}
                  </Tag>
                ))}
              </TagList>
              <Heading>The verdict</Heading>
              <TrustHeading pos={app.trust.trusted}>
                {app.trust.trusted ? "YEAH" : "NAH"}
              </TrustHeading>
            </ColCard>
          </Columns>
        ))}
      </Spacing>
    </Padding>
  );
}
